import {AddIcon, CancelIcon} from "@components/icons";
import {UserGroupSelector} from "@components/UserGroupSelector";
import {Group, Permission, User} from "@core/model";
import {Inbox} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import {useThemeContext} from "@theme";
import {translate} from "@util";

const PermissionsTable = ({
  sx,
  permissions,
  onChange,
}: {
  sx?: SxProps<Theme>;
  permissions: Permission[];
  onChange: (permissions: Permission[]) => void;
}) => {
  const {theme} = useThemeContext();

  const handleUserGroupSelect = (
    index: number,
    v: User | Group,
    type: "USER" | "GROUP",
  ) => {
    const newPermissions = [...permissions];
    newPermissions[index] = {
      operations: newPermissions[index].operations,
      value: v.id!,
      type,
    };
    onChange(newPermissions);
  };

  const handleOperationChange = (
    index: number,
    o: "DELETE" | "READ" | "EDIT",
    checked: boolean,
  ) => {
    let operations = [...(permissions[index].operations ?? [])];
    if (checked) {
      operations.push(o);
    } else {
      operations = operations.filter(temp => temp !== o);
    }

    const newPermissions = [...permissions];
    newPermissions[index] = {
      value: newPermissions[index]!.value,
      type: newPermissions[index]!.type,
      operations,
    };
    onChange(newPermissions);
  };

  const handleAddNewPermission = () => {
    const newPermissions = [...permissions];
    newPermissions.push({
      type: "GROUP",
      value: "",
      operations: ["READ"],
    });

    onChange(newPermissions);
  };

  const handleDeletePermission = (index: number) => {
    const newPermissions = [...permissions];
    newPermissions.splice(index, 1);
    onChange(newPermissions);
  };

  const renderPermission = (p: Permission, index: number) => {
    return (
      <TableRow key={"row_" + index}>
        <TableCell>
          <UserGroupSelector
            selection={{id: p.value, type: p.type}}
            onSelect={(v, t) => handleUserGroupSelect(index, v, t)}
          />
        </TableCell>
        <TableCell align="center">
          <Checkbox
            onChange={(_e, checked) =>
              handleOperationChange(index, "READ", checked)
            }
            checked={p.operations.findIndex(o => o === "READ") !== -1}
          />
        </TableCell>
        <TableCell align="center">
          <Checkbox
            onChange={(_e, checked) =>
              handleOperationChange(index, "EDIT", checked)
            }
            checked={p.operations.findIndex(o => o === "EDIT") !== -1}
          />
        </TableCell>
        <TableCell align="center">
          <Checkbox
            onChange={(_e, checked) =>
              handleOperationChange(index, "DELETE", checked)
            }
            checked={p.operations.findIndex(o => o === "DELETE") !== -1}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton onClick={() => handleDeletePermission(index)}>
            <CancelIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={sx}>
      <TableContainer
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          "& .MuiTableCell-root": {
            py: 1,
            px: 2,
          },
        }}>
        <Table>
          <TableHead
            sx={{
              position: "sticky",
              backgroundImage: "var(--Paper-overlay)",
              bgcolor: theme.palette.background.default,
              top: 0,
              zIndex: 1,
            }}>
            <TableRow>
              <TableCell>{translate("permissions.table.groupUser")}</TableCell>
              <TableCell align="center">
                {translate("permissions.table.canView")}
              </TableCell>
              <TableCell align="center">
                {translate("permissions.table.canEdit")}
              </TableCell>
              <TableCell align="center">
                {translate("permissions.table.canDelete")}
              </TableCell>
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions?.map((p, index) => renderPermission(p, index))}
          </TableBody>
        </Table>
        {permissions && permissions.length == 0 && (
          <Stack direction="column" alignItems="center" gap={2} mt={2}>
            <Inbox color="action" sx={{fontSize: 60}} />
            <Typography color="textPrimary" variant="body1">
              {translate("common.table.noRowsLabel")}
            </Typography>
          </Stack>
        )}
      </TableContainer>
      <Button
        sx={{mt: 1}}
        startIcon={<AddIcon />}
        onClick={handleAddNewPermission}>
        {translate("common.action.add")}
      </Button>
    </Box>
  );
};

export {PermissionsTable};
